import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

const initialState = {
  orders: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  orderHistory: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  marketingExpensesHistory: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  return: {
    list: [],
    perPage: 50,
    total: 0,
    loading: false,
  },
  payments: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  statusList: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  markingExpenses: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  productReport: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
};

// ── Import thunks ─────────────────────────────────────────────────────────────

export const importMeeshoManagementOrders = createAsyncThunk(
  "meesho/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("meesho/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const importMeeshoPayment = createAsyncThunk(
  "meesho/payment/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("meesho/payment/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const importMeeshoMarkingAndExpenses = createAsyncThunk(
  "meesho/ads/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("meesho/ads/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const importMeeshoOutstandingPayments = createAsyncThunk(
  "meesho/import-history/outstanding",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post(
        "meesho/import-history/outstanding",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const importMeeshoPaymentsMarketingAndExpenses = createAsyncThunk(
  "meesho/import-history",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("meesho/import-history", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── History thunks ────────────────────────────────────────────────────────────

export const getMeeshoOrderHistoryList = createAsyncThunk(
  "meesho/order-management/order-history-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/order/import-history/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getMeeshoMarketingExpensesHistoryList = createAsyncThunk(
  "meesho/order-management/order-marketing-expenses-history-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/import-history/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Orders thunks ─────────────────────────────────────────────────────────────

export const getMeeshoOrderManagementOrderList = createAsyncThunk(
  "meesho/order-management/order-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/orders", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const deleteMeeshoOrderManagementOrder = createAsyncThunk(
  "delete-meesho-order-management-order",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`/meesho/orders/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Returns thunks (payment-sheet based) ─────────────────────────────────────

export const getMeeshoOrderReturnList = createAsyncThunk(
  "get-meesho-order-return-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/returns", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Statistics thunks ─────────────────────────────────────────────────────────

export const getMeeshoStatisticsList = createAsyncThunk(
  "get-meesho-statistics-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/statistics", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const getMeeshoStatisticsStatusList = createAsyncThunk(
  "get-meesho-statistics-status-list",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.get(`meesho/${id}/product`, { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Payments thunks ───────────────────────────────────────────────────────────

export const getMeeshoOrderPaymentsList = createAsyncThunk(
  "get-meesho-order-payments-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/payment/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

export const updateMeeshoPaymentVerify = createAsyncThunk(
  "meesho-payment-verify",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.put(`/meesho/payment/verify/${id}`, data);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Marketing thunks ──────────────────────────────────────────────────────────

export const getMeeshoMarkingExpensesList = createAsyncThunk(
  "get-meesho-marking-expenses-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/ads/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Product Report thunks ─────────────────────────────────────────────────────

export const getMeeshoProductReportList = createAsyncThunk(
  "get-meesho-product-report-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/product-report", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Finance Dashboard thunk ───────────────────────────────────────────────────

export const getMeeshoFinanceDashboard = createAsyncThunk(
  "get-meesho-finance-dashboard",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/finance-dashboard", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Payout Breakdown thunk ────────────────────────────────────────────────────

export const getMeeshoPayoutBreakdown = createAsyncThunk(
  "get-meesho-payout-breakdown",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("meesho/payout-breakdown", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const meeshoOrderManagementSlice = createSlice({
  name: "meesho-order-management",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Orders List
      .addCase(getMeeshoOrderManagementOrderList.pending, (state) => {
        state.orders.loading = true;
      })
      .addCase(getMeeshoOrderManagementOrderList.fulfilled, (state, action) => {
        state.orders.loading = false;
        state.orders.list = action?.payload?.data?.data?.orders ?? [];
        state.orders.perPage =
          action?.payload?.data?.data?.pagination?.limit ?? 10;
        state.orders.total =
          action?.payload?.data?.data?.pagination?.total ?? 0;
      })
      .addCase(getMeeshoOrderManagementOrderList.rejected, (state) => {
        state.orders.loading = false;
        state.orders.list = [];
        state.orders.total = 0;
      })

      // Orders Import History
      .addCase(getMeeshoOrderHistoryList.pending, (state) => {
        state.orderHistory.loading = true;
      })
      .addCase(getMeeshoOrderHistoryList.fulfilled, (state, action) => {
        state.orderHistory.loading = false;
        state.orderHistory.list =
          action?.payload?.data?.data?.importHistory ?? [];
        state.orderHistory.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.orderHistory.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMeeshoOrderHistoryList.rejected, (state) => {
        state.orderHistory.loading = false;
        state.orderHistory.list = [];
        state.orderHistory.total = 0;
      })

      // Marketing Expenses History
      .addCase(getMeeshoMarketingExpensesHistoryList.pending, (state) => {
        state.marketingExpensesHistory.loading = true;
      })
      .addCase(getMeeshoMarketingExpensesHistoryList.fulfilled, (state, action) => {
        state.marketingExpensesHistory.loading = false;
        state.marketingExpensesHistory.list =
          action?.payload?.data?.data?.importHistory ?? [];
        state.marketingExpensesHistory.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.marketingExpensesHistory.total =
          action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMeeshoMarketingExpensesHistoryList.rejected, (state) => {
        state.marketingExpensesHistory.loading = false;
        state.marketingExpensesHistory.list = [];
        state.marketingExpensesHistory.total = 0;
      })

      // Returns List (payment-sheet based)
      .addCase(getMeeshoOrderReturnList.pending, (state) => {
        state.return.loading = true;
      })
      .addCase(getMeeshoOrderReturnList.fulfilled, (state, action) => {
        state.return.loading = false;
        state.return.list = action?.payload?.data?.data?.returnList ?? [];
        state.return.perPage =
          action?.payload?.data?.data?.pagination?.limit ?? 50;
        state.return.total =
          action?.payload?.data?.data?.pagination?.total ?? 0;
      })
      .addCase(getMeeshoOrderReturnList.rejected, (state) => {
        state.return.loading = false;
        state.return.list = [];
        state.return.total = 0;
      })

      // Payments List
      .addCase(getMeeshoOrderPaymentsList.pending, (state) => {
        state.payments.loading = true;
      })
      .addCase(getMeeshoOrderPaymentsList.fulfilled, (state, action) => {
        state.payments.loading = false;
        state.payments.list = action?.payload?.data?.data?.payments ?? [];
        state.payments.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.payments.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMeeshoOrderPaymentsList.rejected, (state) => {
        state.payments.loading = false;
        state.payments.list = [];
        state.payments.total = 0;
      })

      // Statistics Status List
      .addCase(getMeeshoStatisticsStatusList.pending, (state) => {
        state.statusList.loading = true;
      })
      .addCase(getMeeshoStatisticsStatusList.fulfilled, (state, action) => {
        state.statusList.loading = false;
        state.statusList.list = action?.payload?.data?.data?.products ?? [];
        state.statusList.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.statusList.total =
          action?.payload?.data?.data?.totalProduct ?? 0;
      })
      .addCase(getMeeshoStatisticsStatusList.rejected, (state) => {
        state.statusList.loading = false;
        state.statusList.list = [];
        state.statusList.total = 0;
      })

      // Marketing & Expenses List
      .addCase(getMeeshoMarkingExpensesList.pending, (state) => {
        state.markingExpenses.loading = true;
      })
      .addCase(getMeeshoMarkingExpensesList.fulfilled, (state, action) => {
        state.markingExpenses.loading = false;
        state.markingExpenses.list =
          action?.payload?.data?.data?.meeshoAdsDetails ?? [];
        state.markingExpenses.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.markingExpenses.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMeeshoMarkingExpensesList.rejected, (state) => {
        state.markingExpenses.loading = false;
        state.markingExpenses.list = [];
        state.markingExpenses.total = 0;
      })

      // Product Report
      .addCase(getMeeshoProductReportList.pending, (state) => {
        state.productReport.loading = true;
      })
      .addCase(getMeeshoProductReportList.fulfilled, (state, action) => {
        state.productReport.loading = false;
        state.productReport.list =
          action?.payload?.data?.data?.paginatedList ?? [];
        state.productReport.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.productReport.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMeeshoProductReportList.rejected, (state) => {
        state.productReport.loading = false;
        state.productReport.list = [];
        state.productReport.total = 0;
      });
  },
});

export default meeshoOrderManagementSlice.reducer;
